@if (visible()) {
  <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
  <div class="tci-backdrop" (click)="close()">
    <div
      class="tci-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tci-title"
      cdkTrapFocus
      [cdkTrapFocusAutoCapture]="true"
      (click)="$event.stopPropagation()"
      (keydown.escape)="close()"
    >
      <to-section-title
        headingId="tci-title"
        [title]="'testCases.import.modalTitle' | translate"
        [subtitle]="'testCases.import.modalSubtitle' | translate"
      >
        <button
          right
          type="button"
          class="tci-header__close"
          [attr.aria-label]="'testCases.import.close' | translate"
          (click)="close()"
        >
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      </to-section-title>

      <div class="tci-body">
        @if (!result()) {
          <ol class="tci-steps" [attr.aria-label]="'testCases.import.stepProgress' | translate">
            @for (s of steps; track s; let last = $last) {
              <li class="tci-steps__item" [attr.aria-current]="step() === s ? 'step' : null">
                <span class="tci-steps__dot" [class.tci-steps__dot--active]="step() >= s" aria-hidden="true">
                  {{ s }}
                </span>
                <span class="tci-steps__label" [class.tci-steps__label--active]="step() >= s">
                  {{ 'testCases.import.steps.' + s | translate }}
                </span>
              </li>
              @if (!last) {
                <li class="tci-steps__line" [class.tci-steps__line--active]="step() > s" aria-hidden="true"></li>
              }
            }
          </ol>
        }

        @if (errorKey(); as key) {
          <p class="tci-error" role="alert">{{ key | translate }}</p>
        }

        @if (step() === 1) {
          <div class="tci-sources" role="radiogroup" [attr.aria-label]="'testCases.import.sourceLegend' | translate">
            @for (option of sources; track option.id) {
              <button
                type="button"
                class="tci-source"
                [id]="'tci-source-' + option.id"
                [class.tci-source--active]="source() === option.id"
                [class.tci-source--disabled]="!option.available"
                role="radio"
                [attr.aria-checked]="source() === option.id"
                [attr.aria-disabled]="!option.available"
                [disabled]="!option.available"
                [attr.tabindex]="rovingTabindex(option)"
                (click)="selectSource(option)"
                (keydown)="onSourceKeydown($event)"
              >
                <span class="tci-source__icon" [class]="'tci-source__icon--' + option.id">
                  <mat-icon aria-hidden="true">{{ option.icon }}</mat-icon>
                </span>
                <span class="tci-source__text">
                  <span class="tci-source__label">
                    {{ 'testCases.import.sources.' + option.id + '.label' | translate }}
                  </span>
                  <span class="tci-source__desc">
                    {{ 'testCases.import.sources.' + option.id + '.desc' | translate }}
                  </span>
                </span>
                @if (option.available) {
                  <span class="tci-source__radio" aria-hidden="true">
                    @if (source() === option.id) {
                      <span class="tci-source__radio-dot"></span>
                    }
                  </span>
                } @else {
                  <span class="tci-source__badge">{{ 'testCases.import.comingSoon' | translate }}</span>
                }
              </button>
            }
          </div>
        }

        @if (step() === 2) {
          <div class="tci-upload">
            <app-file-dropzone
              [accept]="acceptedFileTypes"
              [hint]="'testCases.import.upload.hint' | translate"
              [busy]="busy()"
              (fileSelected)="onFileSelected($event)"
              (cleared)="onFileCleared()"
            />
            <div class="tci-info">
              <mat-icon class="tci-info__icon" aria-hidden="true">info</mat-icon>
              <div class="tci-info__text">
                <strong>{{ 'testCases.import.upload.mappingTitle' | translate }}</strong>
                <p class="tci-info__cols">
                  {{ 'testCases.import.upload.recommended' | translate }}
                  <code>{{ 'testCases.import.upload.columns' | translate }}</code>
                </p>
              </div>
            </div>
            <button type="button" class="tci-template" (click)="downloadTemplate()">
              <mat-icon aria-hidden="true">download</mat-icon>
              {{ 'testCases.import.upload.template' | translate }}
            </button>
          </div>
        }

        @if (step() === 3 && preview(); as p) {
          <div class="tci-preview">
            <div class="tci-summary">
              <span class="tci-summary__item tci-summary__item--total">
                {{ countKey('total', p.totalRows) | translate: { count: p.totalRows } }}
              </span>
              <span class="tci-summary__item tci-summary__item--valid">
                {{ countKey('valid', p.validRowCount) | translate: { count: p.validRowCount } }}
              </span>
              @if (p.errorRowCount > 0) {
                <span class="tci-summary__item tci-summary__item--error">
                  {{ countKey('invalid', p.errorRowCount) | translate: { count: p.errorRowCount } }}
                </span>
              }
            </div>

            @if (p.rows.length > 0) {
              <div class="tci-ptable-wrap">
                <table class="tci-ptable">
                  <thead>
                    <tr>
                      <th scope="col">{{ 'testCases.import.preview.row' | translate }}</th>
                      <th scope="col">{{ 'testCases.import.preview.title' | translate }}</th>
                      <th scope="col">{{ 'testCases.import.preview.priority' | translate }}</th>
                      <th scope="col">{{ 'testCases.import.preview.type' | translate }}</th>
                      <th scope="col">{{ 'testCases.import.preview.assignee' | translate }}</th>
                      <th scope="col">{{ 'testCases.import.preview.action' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of p.rows; track row.rowNumber) {
                      <tr>
                        <td class="tci-ptable__num">{{ row.rowNumber }}</td>
                        <td class="tci-ptable__title">{{ row.title }}</td>
                        <td>
                          @if (row.priority) {
                            {{ 'testCases.priority.' + row.priority | translate }}
                          } @else {
                            <span class="tci-none">—</span>
                          }
                        </td>
                        <td>
                          @if (row.type) {
                            {{ row.type | tcValueLabel: 'type' }}
                          } @else {
                            <span class="tci-none">—</span>
                          }
                        </td>
                        <td>{{ row.assigneeEmail || '—' }}</td>
                        <td>
                          @if (row.existingTestCaseId) {
                            <span class="tci-tag tci-tag--update">
                              {{ 'testCases.import.preview.update' | translate }}
                            </span>
                          } @else {
                            <span class="tci-tag tci-tag--create">
                              {{ 'testCases.import.preview.create' | translate }}
                            </span>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }

            @if (p.errors.length > 0) {
              <div class="tci-errors">
                <div class="tci-errors__title">{{ 'testCases.import.preview.errorsTitle' | translate }}</div>
                <ul class="tci-errors__list">
                  @for (err of p.errors; track $index) {
                    <li class="tci-errors__item">
                      <span class="tci-errors__row">
                        {{ 'testCases.import.preview.rowLabel' | translate: { row: err.rowNumber } }}
                      </span>
                      @if (err.field) {
                        <span class="tci-errors__field">{{ err.field }}</span>
                      }
                      <span class="tci-errors__msg">{{ err.message }}</span>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        }

        @if (result(); as r) {
          <div class="tci-report">
            <span class="tci-report__icon" aria-hidden="true">
              <mat-icon>check_circle</mat-icon>
            </span>
            <h3 class="tci-report__title">{{ 'testCases.import.report.title' | translate }}</h3>
            <div class="tci-report__stats">
              <div class="tci-report__stat tci-report__stat--created">
                <span class="tci-report__value">{{ r.created }}</span>
                <span class="tci-report__label">{{ 'testCases.import.report.created' | translate }}</span>
              </div>
              <div class="tci-report__stat tci-report__stat--updated">
                <span class="tci-report__value">{{ r.updated }}</span>
                <span class="tci-report__label">{{ 'testCases.import.report.updated' | translate }}</span>
              </div>
              <div class="tci-report__stat tci-report__stat--skipped">
                <span class="tci-report__value">{{ r.skipped }}</span>
                <span class="tci-report__label">{{ 'testCases.import.report.skipped' | translate }}</span>
              </div>
            </div>
            @if (r.errors.length > 0) {
              <div class="tci-errors">
                <div class="tci-errors__title">{{ 'testCases.import.report.errorsTitle' | translate }}</div>
                <ul class="tci-errors__list">
                  @for (err of r.errors; track $index) {
                    <li class="tci-errors__item">
                      <span class="tci-errors__row">
                        {{ 'testCases.import.preview.rowLabel' | translate: { row: err.rowNumber } }}
                      </span>
                      @if (err.field) {
                        <span class="tci-errors__field">{{ err.field }}</span>
                      }
                      <span class="tci-errors__msg">{{ err.message }}</span>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        }
      </div>

      <div class="tci-footer">
        @if (result()) {
          <button type="button" class="tci-btn tci-btn--primary" (click)="close()">
            {{ 'testCases.import.report.close' | translate }}
          </button>
        } @else {
          @if (step() > 1) {
            <button type="button" class="tci-btn tci-btn--secondary" [disabled]="busy()" (click)="back()">
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
              {{ 'testCases.import.back' | translate }}
            </button>
          }
          <button type="button" class="tci-btn tci-btn--secondary" (click)="close()">
            {{ 'testCases.import.cancel' | translate }}
          </button>
          <button type="button" class="tci-btn tci-btn--primary" [disabled]="!canProceed()" (click)="primaryAction()">
            @if (busy()) {
              <mat-spinner diameter="16" />
            }
            @if (step() < 3) {
              {{ 'testCases.import.next' | translate }}
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            } @else {
              <mat-icon aria-hidden="true">check</mat-icon>
              {{ 'testCases.import.start' | translate }}
            }
          </button>
        }
      </div>
    </div>
  </div>
}
